import { isSystemEventMessage, parseMessageBodyText } from "@/lib/realtime/chat-message-helpers";
import { parseAttachments } from "@/lib/realtime/message-attachments";
import { formatWaGroupSystemLine } from "@/lib/messages/wa-groups";
import { getMessageText, isWhatsAppDeletedMessage } from "@/lib/messages/messages-helpers";

/**
 * Pure helpers for "Edit message" (internal Chats + WhatsApp) and the internal "Delete for
 * everyone" (unsend). Framework-free so the gating and the optimistic patches can be pinned by
 * unit tests; the hook and the cluster list both consume these so they never disagree about what
 * counts as editable.
 *
 * Wire contract (healo_backend):
 *   - a successful edit stamps `metadata.edited = {at, by}`; an inbound PATIENT edit relayed by
 *     the WhatsApp gateway carries `metadata.whatsapp.edited === true` (+ `editTimestamp`) instead.
 *   - an outbound WhatsApp edit we requested is `metadata.whatsapp.editPending === true` with the
 *     attempted text in `pendingEdit.text`; the gateway's verdict either applies it (pending flags
 *     cleared, `metadata.edited` stamped) or leaves `editFailed: true` + `editError`.
 *     `pendingEdit` is KEPT on failure (a late success may still apply), so pending rendering gates
 *     on `editPending === true` — never on `pendingEdit` presence.
 */

/** WhatsApp lets a sender edit a message for 15 minutes after sending it (enforced server-side too). */
export const WHATSAPP_EDIT_WINDOW_MS = 15 * 60 * 1000;

/**
 * `{at, by}` for a message that has been edited, or null. The server stamp wins; an inbound
 * patient edit only carries the gateway's `whatsapp.edited` flag, whose timestamp may be missing.
 */
export function getEditedInfo(message) {
  const md = message?.metadata;
  const edited = md?.edited;
  if (edited && typeof edited === "object" && typeof edited.at === "string" && edited.at.trim()) {
    return { at: edited.at, by: String(edited.by || "") };
  }
  if (md?.whatsapp?.edited === true) {
    const ts = md.whatsapp.editTimestamp;
    return { at: typeof ts === "string" ? ts : "", by: "" };
  }
  return null;
}

export function isMessageEdited(message) {
  return getEditedInfo(message) != null;
}

/** An edit WE requested is still waiting on WhatsApp's verdict. */
export function isWhatsAppEditPending(message) {
  return message?.metadata?.whatsapp?.editPending === true;
}

/** WhatsApp refused (or never confirmed) an edit we requested — the patient still sees the original. */
export function isWhatsAppEditFailed(message) {
  return message?.metadata?.whatsapp?.editFailed === true;
}

/** Why WhatsApp refused the edit, as reported by the gateway. "" when we have no detail. */
export function getWhatsAppEditError(message) {
  const reason = message?.metadata?.whatsapp?.editError;
  return typeof reason === "string" ? reason.trim() : "";
}

/** The text an in-flight WhatsApp edit is trying to apply ("" when none). */
export function getWhatsAppPendingEditText(message) {
  const text = message?.metadata?.whatsapp?.pendingEdit?.text;
  return typeof text === "string" ? text : "";
}

/**
 * Bubble text. While a WhatsApp edit is pending the bubble shows what the patient is ABOUT to see
 * (the pending text) so the actor's screen reflects their action immediately — the same optimism
 * as revoke's strike-through. Otherwise the regular message text.
 */
export function getMessageDisplayText(message) {
  if (isWhatsAppEditPending(message)) {
    const pending = getWhatsAppPendingEditText(message).trim();
    if (pending) return pending;
  }
  return getMessageText(message);
}

/**
 * Rows that are not conversational messages — system/event lines, auto replies, group system
 * lines, call logs. Never editable or unsendable (the server refuses them too).
 */
export function isSystemLikeMessage(message) {
  if (String(message?.sender?.kind || "").toLowerCase() === "system") return true;
  const md = message?.metadata;
  if (md?.isSystem === true || md?.autoReply === true) return true;
  const ct = String(message?.contentType || "").trim().toLowerCase();
  if (ct.startsWith("thread.") || ct === "group.system" || ct === "call.log") return true;
  return Boolean(isSystemEventMessage(message));
}

/** Non-blank conversational text — string bodies and `{text}` documents; event bodies never count. */
export function hasMessageText(message) {
  const body = message?.body;
  if (typeof body === "string") return body.trim().length > 0;
  if (body && typeof body === "object" && !Array.isArray(body)) {
    if (typeof body.event === "string") return false;
    return typeof body.text === "string" && body.text.trim().length > 0;
  }
  return false;
}

export function messageHasAttachments(message) {
  return parseAttachments(message).length > 0;
}

/**
 * Inside WhatsApp's edit window, measured from the message's createdAt (server insert time, so it
 * is conservative in the right direction). An unparsable createdAt is "no" — never offer an edit
 * we cannot reason about; a future createdAt (clock skew) counts as within.
 */
export function isWithinWhatsAppEditWindow(message, nowMs = Date.now()) {
  const t = Date.parse(String(message?.createdAt || ""));
  if (!Number.isFinite(t)) return false;
  return nowMs - t <= WHATSAPP_EDIT_WINDOW_MS;
}

/**
 * Internal Chats "delete for everyone" (unsend): the message must have reached the server (an id —
 * optimistic placeholders have none), and must be a real conversational row. Authorship is the
 * cluster's call (own messages only); the server re-checks it.
 */
export function canUnsendInternalMessage(message) {
  if (!String(message?.id || "").trim()) return false;
  if (message?.metadata?.optimistic === true) return false;
  return !isSystemLikeMessage(message);
}

/** Internal edit: unsendable AND has text (attachment-only messages have nothing to edit here). */
export function canEditInternalMessage(message) {
  return canUnsendInternalMessage(message) && hasMessageText(message);
}

/**
 * WhatsApp edit: text-only, ours, not deleted, no edit already in flight, and inside the 15-minute
 * window. `fromMe !== false` is deliberately lenient — our own sends carry no `metadata.whatsapp`
 * at all (only bridged inbound messages and the revoke/edit stamps create it), so the absence of
 * the key means "ours". There is NO `externalId` requirement: the gateway reports the wamid without
 * a message broadcast, so the live client never learns it for a fresh send — exactly the messages
 * inside the window. The server answers 409 when the message has not reached WhatsApp yet and the
 * hook rolls the optimistic edit back.
 */
export function canEditWhatsAppMessage(message, { nowMs = Date.now() } = {}) {
  if (!canUnsendInternalMessage(message)) return false;
  if (!hasMessageText(message)) return false;
  if (messageHasAttachments(message)) return false;
  if (message?.metadata?.whatsapp?.fromMe === false) return false;
  if (isWhatsAppDeletedMessage(message)) return false;
  if (isWhatsAppEditPending(message)) return false;
  return isWithinWhatsAppEditWindow(message, nowMs);
}

/**
 * Optimistic internal edit — mirrors the server's EditMessageText `$set`: new body text (keeping
 * the other body keys), `metadata.edited = {at, by}`, and `metadata.mentions` replaced (dropped
 * when empty). Never mutates the input.
 */
export function applyOptimisticInternalEdit(message, { text, mentions = [], byUserId = "", at } = {}) {
  const body = message?.body;
  const nextBody =
    body && typeof body === "object" && !Array.isArray(body) ? { ...body, text } : { text };
  const metadata = { ...(message?.metadata || {}), edited: { at, by: byUserId } };
  if (Array.isArray(mentions) && mentions.length) {
    metadata.mentions = [...mentions];
  } else {
    delete metadata.mentions;
  }
  return { ...message, body: nextBody, metadata };
}

/**
 * Optimistic WhatsApp edit — the pending stamp the server writes before asking the gateway. The
 * body is NOT touched (the patient still sees the original until WhatsApp confirms); the bubble
 * shows `pendingEdit.text` through getMessageDisplayText. Never mutates the input.
 */
export function applyOptimisticWhatsAppEdit(message, { text, byUserId = "", at } = {}) {
  const md = message?.metadata || {};
  return {
    ...message,
    metadata: {
      ...md,
      whatsapp: {
        ...(md.whatsapp || {}),
        editPending: true,
        pendingEdit: { text, by: byUserId, at },
        editFailed: false,
        editError: "",
      },
    },
  };
}

/**
 * Does `message` still carry OUR optimistic edit stamp (`at` = the client timestamp we stamped)?
 * A rollback after a failed request must only revert a bubble that still shows the optimistic
 * state — if the server committed and its update frame already landed, that frame carries the
 * server's own timestamp and must not be clobbered with the pre-edit copy.
 */
export function hasOptimisticEditStamp(message, at) {
  if (!at) return false;
  const md = message?.metadata;
  return md?.edited?.at === at || md?.whatsapp?.pendingEdit?.at === at;
}

/**
 * Undo an optimistic edit on `current`: restore the body, `metadata.edited`, `metadata.mentions`
 * and `metadata.whatsapp` from the pre-edit `original`, keeping everything else (receipts,
 * reactions) the row may have gained meanwhile. Never mutates its inputs.
 */
export function revertOptimisticEdit(current, original) {
  const metadata = { ...(current?.metadata || {}) };
  const originalMd = original?.metadata || {};
  for (const key of ["edited", "mentions", "whatsapp"]) {
    if (originalMd[key] === undefined) delete metadata[key];
    else metadata[key] = originalMd[key];
  }
  return { ...current, body: original?.body, metadata };
}

/**
 * Thread-row patch for an `update:true` frame: only when the row's last message IS the updated
 * message. Keeps the live reactions re-classification and refreshes the preview text so an edit
 * of a row's last message shows in the list without waiting for the next overview refresh.
 * Returns the row by identity when it is not affected (so list patchers can skip it).
 */
export function patchThreadRowForMessageUpdate(row, msg) {
  if (String(row?.lastMessageId || "") !== String(msg?.id || "")) return row;
  const preview =
    String(msg?.contentType || "").trim().toLowerCase() === "group.system"
      ? formatWaGroupSystemLine(msg)
      : parseMessageBodyText(msg?.body);
  return {
    ...row,
    lastMessageReactions: msg?.reactions ?? null,
    lastMessagePreview: preview || row.lastMessagePreview,
  };
}

/**
 * Put a message back into an ascending-by-createdAt history (rollback of an optimistic unsend).
 * No-op (same array) when a row with that id is already present — the server's update frame may
 * have re-delivered it meanwhile. Appends when createdAt cannot be compared.
 */
export function insertMessageSortedByCreatedAt(list, message) {
  if (!Array.isArray(list)) return list;
  const mid = String(message?.id || "").trim();
  if (!mid) return list;
  if (list.some((m) => String(m?.id || "").trim() === mid)) return list;
  const t = Date.parse(String(message?.createdAt || ""));
  if (!Number.isFinite(t)) return [...list, message];
  let idx = list.length;
  for (let i = 0; i < list.length; i += 1) {
    const other = Date.parse(String(list[i]?.createdAt || ""));
    if (Number.isFinite(other) && other > t) {
      idx = i;
      break;
    }
  }
  return [...list.slice(0, idx), message, ...list.slice(idx)];
}
